import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Camera, Loader2, X, UserRound } from "lucide-react";
import { toast } from "sonner";

const cache = new Map();

export function UserAvatar({ path, name, className = "h-10 w-10", rounded = "rounded-full" }) {
  const [src, setSrc] = useState(path ? cache.get(path) : null);
  useEffect(() => {
    if (!path) { setSrc(null); return; }
    if (cache.has(path)) { setSrc(cache.get(path)); return; }
    let alive = true;
    http.get(`/files/${encodeURI(path)}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); cache.set(path, u); if (alive) setSrc(u); })
      .catch(() => {});
    return () => { alive = false; };
  }, [path]);
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={src} alt={name} className={`${className} ${rounded} object-cover shrink-0`} />
  ) : (
    <div className={`${className} ${rounded} shrink-0 bg-slate-800 border border-slate-700 flex items-center justify-center font-display font-bold text-slate-300`}>
      {name ? initials : <UserRound size={18} />}
    </div>
  );
}

export function PhotoField({ value, onChange, name, kind = "avatar" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/uploads?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      cache.set(data.storage_path, URL.createObjectURL(file));
      onChange(data.storage_path);
    } catch { toast.error("Fotoğraf yüklenemedi"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-4" data-testid="user-photo-field">
      <div className="relative">
        <UserAvatar path={value} name={name} className="h-20 w-20" rounded="rounded-2xl" />
        {busy && <div className="absolute inset-0 rounded-2xl bg-black/60 flex items-center justify-center"><Loader2 className="animate-spin text-blue-300" /></div>}
      </div>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => ref.current?.click()} data-testid="user-photo-upload"
                className="px-3 py-2 rounded-lg bg-blue-600/15 border border-blue-500/30 text-blue-200 text-sm font-semibold flex items-center gap-2 hover:bg-blue-600/25 transition-colors">
          <Camera size={15} /> {value ? "Fotoğrafı Değiştir" : "Fotoğraf Yükle"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} data-testid="user-photo-remove"
                  className="px-3 py-1.5 rounded-lg text-xs text-red-300 hover:bg-red-500/10 flex items-center gap-1"><X size={12} /> Kaldır</button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="user-photo-input" />
    </div>
  );
}
